import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ShoppingBag } from "lucide-react";
import { Logo } from "./Logo";
import { navLinks, restaurant, fullAddress } from "../../data/siteData";
import { useCartTotals } from "../../lib/cart";
import { getLenis } from "../../lib/lenis";

const MobileMenu = ({ onClose }) => (
  <motion.div
    initial={{ clipPath: "inset(0 0 100% 0)" }}
    animate={{ clipPath: "inset(0 0 0% 0)" }}
    exit={{ clipPath: "inset(0 0 100% 0)" }}
    transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
    className="fixed inset-0 z-40 bg-[#0B2216] flex flex-col pt-24 px-6 pb-8 xl:hidden overflow-y-auto"
    data-testid="mobile-menu"
  >
    <nav className="flex flex-col">
      {navLinks.map((l, i) => (
        <motion.div key={l.to} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.05 }}>
          <NavLink
            to={l.to}
            end={l.to === "/"}
            onClick={onClose}
            data-testid={`mobile-nav-link-${l.id}`}
            className={({ isActive }) => `flex items-baseline gap-4 py-2.5 border-b border-[#D4A359]/10 font-display text-[1.7rem] sm:text-4xl ${isActive ? "text-[#E5B66C] italic" : "text-[#F7F4EB]"}`}
          >
            <span className="text-xs font-sans not-italic text-[#D4A359]">0{i + 1}</span>
            {l.label}
          </NavLink>
        </motion.div>
      ))}
    </nav>
    <div className="mt-auto pt-8 space-y-3 text-sm text-[#D6CFC0]">
      <p>{fullAddress}</p>
      <a href={restaurant.phoneHref} className="btn-gold w-full justify-center" data-testid="mobile-menu-call">
        <Phone className="w-4 h-4" /> {restaurant.phone}
      </a>
    </div>
  </motion.div>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { count } = useCartTotals();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const l = getLenis();
    if (open) l?.stop();
    else l?.start();
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,padding] duration-500 border-b ${scrolled || open ? "bg-[#0B2216]/85 backdrop-blur-xl border-[#D4A359]/15 py-3" : "bg-transparent border-transparent py-5"}`}
      >
        <div className="container-site flex items-center justify-between gap-6">
          <Logo />
          <nav className="hidden xl:flex items-center" data-testid="desktop-nav">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) => `nav-link px-2.5 py-2 text-[13px] whitespace-nowrap ${isActive ? "is-active text-[#E5B66C]" : "text-[#D6CFC0] hover:text-[#F7F4EB]"}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/commander-a-emporter" className="relative w-11 h-11 rounded-full border border-[#D4A359]/40 text-[#F7F4EB] flex items-center justify-center hover:bg-[#D4A359]/15 transition-colors" aria-label="Voir le panier" data-testid="header-cart-button">
              <ShoppingBag className="w-4 h-4" />
              {count > 0 && <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[#C85A32] text-[11px] font-semibold flex items-center justify-center" data-testid="header-cart-count">{count}</span>}
            </Link>
            <a href={restaurant.phoneHref} className="w-11 h-11 rounded-full bg-[#D4A359] text-[#0B2216] flex items-center justify-center hover:bg-[#E5B66C] transition-colors" aria-label={`Appeler le ${restaurant.phone}`} data-testid="header-call-button">
              <Phone className="w-4 h-4" />
            </a>
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="xl:hidden shrink-0 w-11 h-11 rounded-full border border-[#D4A359]/40 flex items-center justify-center text-[#F7F4EB]"
              aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={open}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
